import Chart from 'react-google-charts';
import { ChartDataLoaded } from '@g17eco/types/insight-custom-dashboard';
import { chartStyleNames, getChartOptions, toChartProps } from '@routes/summary/insights/utils/chartStyles';
import { ChartType, chartTooltipOptions } from '@routes/summary/insights/utils/constants';
import { TakenFromText } from './common/TakenFromText';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps } from '../types';
import { Note } from './common/Note';
import { FormulaVariables } from '@utils/formula';
import { useChartData } from './useChartData';
import { getVAxisTitle, hasChartDataRows } from './multi-utrs-chart/utils';

export interface BarChartDataLoaded<T = unknown> extends ChartDataLoaded<T> {
  variablesWithValues?: FormulaVariables;
}

export type Props<T extends BaseHistoricalUtr> = BaseChartComponentProps<T> & {
  getChartData: (props: ChartDataProps<T>) => Promise<BarChartDataLoaded>;
  /**
   * Stacked horizontal-bar mode. Swaps the snapshot preset (which hides legend +
   * hAxis text because each bar is self-labelled) for the stacked preset so users
   * can read the value axis and identify stacked categories.
   */
  isStacked?: boolean;
  /**
   * Opt in to a value-axis title (the metric unit / %). Off by default so existing
   * bar charts keep their original no-title appearance; breakout charts opt in.
   */
  showValueAxisTitle?: boolean;
  /**
   * Explicit axis-title override. Takes precedence over the computed title. Pass
   * '' to render with no title without falling back to the metric unit (breakout
   * charts use this for value columns that have no `unit` field).
   */
  vAxisTitle?: string;
  /**
   * Opt out of the "Taken from: …" caption. Breakout charts use this because their
   * synthesized data has no scalar effectiveDate. Legacy callers leave it unset and
   * the caption renders with effectiveDate defaulting to '' (matches pre-breakout).
   */
  hideTakenFromText?: boolean;
};

export const BarChart = <T extends BaseHistoricalUtr>(props: Props<T>) => {
  const {
    getChartData,
    utrsData,
    variables,
    note,
    overrideOptions,
    unitText,
    calculation,
    isStacked,
    showValueAxisTitle,
    vAxisTitle: vAxisTitleOverride,
    hideTakenFromText,
  } = props;
  const data = useChartData(getChartData, props);

  if (!utrsData.length || !variables || !calculation || !data || !hasChartDataRows(data.chartData)) {
    return null;
  }

  const { chartData, effectiveDate = '', period, aggregated, variablesWithValues } = data;
  // For horizontal BarCharts the value axis is hAxis, but chartStyles' param is named
  // vAxisTitle for historical reasons — same string, different axis applied internally.
  // Explicit override wins; otherwise opt-in to the computed title (off by default so
  // legacy bar charts keep their original no-title appearance).
  const vAxisTitle = vAxisTitleOverride ?? (showValueAxisTitle ? getVAxisTitle({ unitText, calculation, utrsData }) : undefined);

  return (
    <>
      {variablesWithValues ? <Note note={note} variablesWithValues={variablesWithValues} /> : null}
      <div className='w-100 h-100' data-testid='bar-chart'>
        <Chart
          chartType={ChartType.BarChart}
          data={chartData}
          width={'100%'}
          height={'100%'}
          options={{
            ...getChartOptions({
              chartType: ChartType.BarChart,
              // Snapshot bars are self-labelled; stacked bars need the legend + axis.
              options: isStacked ? chartStyleNames.chartStyleStacked : chartStyleNames.chartStyleHorizontalBar,
              vAxisTitle,
              chartProps: toChartProps(props),
            }),
            tooltip: chartTooltipOptions,
            ...overrideOptions,
          }}
        />
      </div>
      {hideTakenFromText ? null : <TakenFromText effectiveDate={effectiveDate} period={period} aggregated={aggregated} />}
    </>
  );
};
